import { apiClient } from './client'
import { ApiError } from './errors'
import {
  RUNNABLE_ARMS,
  type ArmSummary,
  type DocumentVariant,
  type LlmCall,
  type LlmModel,
  type LlmUsageSummary,
  type RecognitionArm,
} from '@/types/domain'

const http = () => apiClient.axios

/**
 * The model side of a template: which models may be chosen, what the runs
 * have cost, what each recognition arm read, and adopting one of those
 * readings as the delivered values.
 */

export interface LlmCallLog {
  calls: LlmCall[]
  /** Totals over exactly the calls returned beside it. `truncated` says the
   *  listing hit its limit, which is the difference between "this template
   *  cost $2.10" and "the last 100 calls cost $2.10". */
  summary: LlmUsageSummary
}

/** What promoting an arm changed. `promoted` counts documents. */
export interface PromoteResult {
  promoted: number
  arm: string
  model: string
}

/**
 * Options for a run of one recognition arm.
 *
 * `apply` is deliberately **required**. The worker defaults it to true
 * (`job.get("apply", True)`), so a comparison run started without it
 * rewrites the delivered values — and the next run then reads its own output
 * as the OCR baseline, agrees with itself and reports confidence 1.0 on a
 * wrong value. A boolean whose default silently corrupts data has no
 * business being optional here; the caller has to say which kind of run this
 * is.
 */
export interface ArmRunOptions {
  documentIds: string[]
  arm: RecognitionArm
  /** True adopts the reading; false stores it beside the delivered values
   *  for comparison (and `promote` adopts it later, if it wins). */
  apply: boolean
  /** Overrides the template's `llmModel` and the deployment default. The
   *  gateway validates it against the price table (400 otherwise). */
  model?: string
}

export const llmApi = {
  /** The models the picker may offer: the price table's keys, each with its
   *  rates, so a choice can be costed before it is made. */
  async models(): Promise<LlmModel[]> {
    const { data } = await http().get<{ models?: LlmModel[] }>('/v1/llm/models')
    return data.models ?? []
  },

  /** The template's LLM audit trail, newest first, with the priced summary
   *  the gateway computes over exactly these rows. */
  async calls(templateId: string, options: { limit?: number } = {}): Promise<LlmCallLog> {
    const { data } = await http().get<{ calls?: LlmCall[]; summary?: LlmUsageSummary }>(
      `/v1/templates/${templateId}/llm-calls`,
      { params: options.limit != null ? { limit: options.limit } : undefined },
    )
    return {
      calls: data.calls ?? [],
      summary: data.summary ?? emptyUsageSummary(),
    }
  },

  /**
   * Per arm *and* model: coverage, review burden, tokens, estimated cost and
   * what adopting it would change against the delivered values.
   *
   * Two things about the money. `costShared` marks an arm whose calls another
   * arm on the same model may also be billed for (the page-pass family shares
   * one `purpose` in the audit trail), so its cost and tokens are an upper
   * bound and want a qualifier beside them. And the figures are **lower** than
   * they were for existing templates: field detection and the record check are
   * no longer added to the page pass's bill. That is a correction, not data
   * that went missing.
   */
  async arms(templateId: string): Promise<ArmSummary[]> {
    const { data } = await http().get<{ arms?: ArmSummary[] }>(`/v1/templates/${templateId}/arms`)
    return data.arms ?? []
  },

  /** Every arm stored for one document — the review screen's "Lesungen".
   *  Each variant names its `model`, which is the only way to tell two models
   *  on one arm apart; it is empty for `ocr` and `human`, which run none. */
  async documentVariants(templateId: string, documentId: string): Promise<DocumentVariant[]> {
    const { data } = await http().get<{ variants?: DocumentVariant[] }>(
      `/v1/templates/${templateId}/documents/${documentId}/variants`,
    )
    return data.variants ?? []
  },

  /**
   * Makes a stored reading the delivered values.
   *
   * This is the other half of `apply: false`: a comparison run changes
   * nothing, and adopting the winner afterwards would otherwise mean opening
   * every document and confirming every field. Reversible by promoting
   * `ocr`, which is frozen before any model ran. The gateway refuses
   * `human` (400 `arm_not_promotable`) — reviewed values are written by
   * reviewing.
   */
  async promote(
    templateId: string,
    input: { arm: RecognitionArm; model?: string; documentIds?: string[] },
  ): Promise<PromoteResult> {
    const { data } = await http().post<PromoteResult>(
      `/v1/templates/${templateId}/variants/promote`,
      {
        arm: input.arm,
        ...(input.model ? { model: input.model } : {}),
        ...(input.documentIds?.length ? { documentIds: input.documentIds } : {}),
      },
    )
    return data
  },

  /**
   * Starts one arm over a selection of documents and returns the job id.
   *
   * The arm is checked here as well as in the gateway: a value outside
   * `models.RunnableArms` is a 400 with no useful German text, and the app
   * would show it verbatim.
   */
  async startArmRun(templateId: string, options: ArmRunOptions): Promise<string> {
    if (!RUNNABLE_ARMS.includes(options.arm)) {
      throw new ApiError({
        status: 400,
        code: 'unknown_arm',
        message: `Diese Leseart kann nicht ausgeführt werden: ${options.arm}`,
      })
    }
    const { data } = await http().post<{ job_id?: string }>(`/v1/templates/${templateId}/jobs`, {
      documentIds: options.documentIds,
      arm: options.arm,
      apply: options.apply,
      ...(options.model ? { model: options.model } : {}),
    })
    return data.job_id ?? ''
  },
}

/** An answer with no `summary` (an older gateway, or an empty template) still
 *  has to render a totals row rather than crash it. */
function emptyUsageSummary(): LlmUsageSummary {
  return {
    total: {
      calls: 0,
      failed: 0,
      inputTokens: 0,
      outputTokens: 0,
      cacheWriteTokens: 0,
      cacheReadTokens: 0,
      cost: {
        inputUsd: 0,
        outputUsd: 0,
        cacheWriteUsd: 0,
        cacheReadUsd: 0,
        totalUsd: 0,
        priced: false,
      },
      unpricedCalls: 0,
    },
    groups: [],
  }
}
